import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import {
  adminListPlans,
  adminSavePlan,
  adminListSubscriptions,
} from "@/lib/api/subscriptions.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/admin/subscriptions")({
  component: AdminSubscriptionsPage,
});

function AdminSubscriptionsPage() {
  const list = useServerFn(adminListPlans);
  const save = useServerFn(adminSavePlan);
  const listSubs = useServerFn(adminListSubscriptions);
  const qc = useQueryClient();

  const plansQ = useQuery({ queryKey: ["admin-plans"], queryFn: () => list() });
  const subsQ = useQuery({ queryKey: ["admin-subs"], queryFn: () => listSubs() });

  const saveM = useMutation({
    mutationFn: (input: any) => save({ data: input }),
    onSuccess: () => {
      toast.success("Plan saved");
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      qc.invalidateQueries({ queryKey: ["sub-plans"] });
    },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });

  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Subscription plans & members</h2>
      </header>

      <section>
        <h3 className="mb-3 font-display text-lg text-navy">Plans</h3>
        {plansQ.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <div className="space-y-4">
            {(plansQ.data?.plans ?? []).map((p: any) => (
              <PlanEditor key={p.code} plan={p} onSave={(d) => saveM.mutate(d)} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-3 font-display text-lg text-navy">Member subscriptions</h3>
        {subsQ.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Period end</th>
                  <th className="px-3 py-2 text-left">Trial ends</th>
                  <th className="px-3 py-2 text-left">Last order</th>
                </tr>
              </thead>
              <tbody>
                {(subsQ.data?.rows ?? []).map((r: any) => (
                  <tr key={r.user_id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.full_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </td>
                    <td className="px-3 py-2">{r.plan_code || "—"}</td>
                    <td className="px-3 py-2 capitalize">{r.status}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.current_period_end
                        ? new Date(r.current_period_end).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.trial_ends_at
                        ? new Date(r.trial_ends_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.last_order_reference || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function PlanEditor({
  plan,
  onSave,
}: {
  plan: any;
  onSave: (input: any) => void;
}) {
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? "");
  const [price, setPrice] = useState<string>(String(plan.monthly_price_zar ?? "0"));
  const [features, setFeatures] = useState<string>((plan.features ?? []).join("\n"));
  const [isActive, setIsActive] = useState<boolean>(!!plan.is_active);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">{plan.code}</p>
          <h4 className="font-display text-lg text-navy">{name}</h4>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Visible to members
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-muted-foreground">Name</span>
          <input
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Monthly price (ZAR)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs text-muted-foreground">Description</span>
          <textarea
            rows={2}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs text-muted-foreground">Features (one per line)</span>
          <textarea
            rows={5}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            value={features}
            onChange={(e) => setFeatures(e.target.value)}
          />
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() =>
            onSave({
              code: plan.code,
              name,
              description,
              monthlyPriceZar: Number(price) || 0,
              features: features
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
              isActive,
            })
          }
          className="inline-flex items-center gap-2 rounded-md bg-navy px-3 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
        >
          <Save className="h-4 w-4" /> Save plan
        </button>
      </div>
    </div>
  );
}
